/**
 * All Deals Modal Component
 * Full-screen modal showing all available promotions with search and filters
 */

import React, { useState, useMemo } from 'react';
import { X, Search, Plus, Tag, TrendingDown, Calendar, Filter, MapPin, Package } from 'lucide-react';
import type { NettoDeal } from '../services/nettoDealsService';

interface AllDealsModalProps {
  isOpen: boolean;
  onClose: () => void;
  deals: NettoDeal[];
  onAddDeal: (deal: NettoDeal) => void;
  catalogueName?: string;
  validFrom?: string;
  validUntil?: string;
}

const CATEGORIES = [
  'Tous',
  'Frais',
  'Épicerie',
  'Fruits & Légumes',
  'Boucherie',
  'Surgelés',
  'Boissons',
  'Hygiène',
  'Droguerie',
  'Traiteur'
];

export function AllDealsModal({
  isOpen,
  onClose,
  deals,
  onAddDeal,
  catalogueName = 'Catalogue Netto',
  validFrom,
  validUntil
}: AllDealsModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tous');

  // Filter and search deals
  const filteredDeals = useMemo(() => {
    return deals.filter(deal => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = 
        deal.product.toLowerCase().includes(searchLower) ||
        deal.brand?.toLowerCase().includes(searchLower) ||
        deal.description?.toLowerCase().includes(searchLower);
      const matchesCategory = selectedCategory === 'Tous' || deal.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [deals, searchQuery, selectedCategory]);

  // Group deals by category
  const dealsByCategory = useMemo(() => {
    const grouped: Record<string, NettoDeal[]> = {};
    filteredDeals.forEach(deal => {
      if (!grouped[deal.category]) {
        grouped[deal.category] = [];
      }
      grouped[deal.category].push(deal);
    });
    return grouped;
  }, [filteredDeals]);

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'long',
      year: 'numeric'
    });
  };

  if (!isOpen) return null;

  const handleAddDeal = (deal: NettoDeal) => {
    onAddDeal(deal);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 p-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                <Tag className="text-red-600" size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{catalogueName}</h2>
                <div className="flex items-center gap-2 text-red-100 text-sm">
                  <Calendar size={14} />
                  <span>
                    {validFrom && validUntil 
                      ? `Valable du ${formatDate(validFrom)} au ${formatDate(validUntil)}`
                      : 'Promotions en cours'
                    }
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
            >
              <X className="text-white" size={24} />
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un produit, une marque..."
              className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-white/20 bg-white/10 text-white placeholder-white/60 focus:outline-none focus:bg-white/20"
            />
          </div>
        </div>

        {/* Category Filters */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0 overflow-x-auto">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-600" />
            {CATEGORIES.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors ${
                  selectedCategory === category
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Deals List */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredDeals.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">Aucune promotion trouvée</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(dealsByCategory).map(([category, categoryDeals]) => (
                <div key={category}>
                  <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <span className="w-1 h-6 bg-red-600 rounded"></span>
                    {category}
                    <span className="text-sm font-normal text-gray-500">
                      ({categoryDeals.length})
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {categoryDeals.map((deal, index) => (
                      <button
                        key={index}
                        onClick={() => handleAddDeal(deal)}
                        className="bg-white rounded-xl p-4 border-2 border-gray-100 hover:border-red-400 hover:shadow-lg transition-all text-left group"
                        title={deal.description}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            {/* Brand badge */}
                            {deal.brand && (
                              <span className="inline-block bg-slate-100 text-slate-700 text-xs font-bold px-2 py-1 rounded mb-2">
                                {deal.brand}
                              </span>
                            )}
                            
                            {/* Product name */}
                            <h4 className="font-bold text-slate-900 group-hover:text-red-600 transition-colors mb-2">
                              {deal.product}
                            </h4>
                            
                            {/* Size */}
                            {deal.size && (
                              <div className="flex items-center gap-1 text-gray-600 text-xs mb-2">
                                <Package size={12} />
                                <span>{deal.size}</span>
                              </div>
                            )}
                            
                            {/* Price info */}
                            <div className="space-y-1 mb-2">
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1 text-red-600 font-semibold text-sm">
                                  <TrendingDown size={14} />
                                  <span>{deal.discount}</span>
                                </div>
                                
                                {deal.originalPrice && (
                                  <span className="text-gray-400 text-xs line-through">
                                    {deal.originalPrice}
                                  </span>
                                )}
                                
                                {deal.price && (
                                  <span className="text-green-700 font-bold text-sm">
                                    {deal.price}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Location */}
                            {deal.location && (
                              <div className="flex items-start gap-1 text-gray-500 text-xs mb-1">
                                <MapPin size={11} className="mt-0.5 flex-shrink-0" />
                                <span className="line-clamp-1">{deal.location}</span>
                              </div>
                            )}
                            
                            {/* Valid until */}
                            <div className="flex items-center gap-1 text-gray-500 text-xs">
                              <Calendar size={12} />
                              <span>
                                Jusqu'au {new Date(deal.validUntil).toLocaleDateString('fr-FR', { 
                                  day: '2-digit', 
                                  month: '2-digit' 
                                })}
                              </span>
                            </div>
                          </div>
                          
                          {/* Add button */}
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-red-100 group-hover:bg-red-600 rounded-lg flex items-center justify-center transition-colors">
                              <Plus className="text-red-600 group-hover:text-white" size={18} />
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
          <p className="text-sm text-gray-600">
            {filteredDeals.length} promotion{filteredDeals.length > 1 ? 's' : ''} affichée{filteredDeals.length > 1 ? 's' : ''}
          </p>
          <button
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold px-6 py-2 rounded-lg transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
